import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Upload, Mic, MicOff, FileText, X,
  CheckCircle2, XCircle, AlertTriangle, Clock, Zap, RefreshCw,
  Inbox as InboxIcon, ChevronRight, Trash2, Edit3, Save,
  Image as ImageIcon, FileAudio, ArrowRight,
  Mail, Contact,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { PageShell, PageHeader } from "@/components/altiora/PremiumUI";
import { useEffect, useRef, useState } from "react";

const NAVY      = "#0B2545";
const NAVY_DEEP = "#081A33";
const CHAMPAGNE = "#C5A880";
const GOLD      = "#F5D687";
const GREEN     = "#0B7A3B";
const AMBER     = "#B45309";
const CRIMSON   = "#B91C1C";
const CREAM     = "#FBF6EA";

const STATUS_META = {
  processing:            { fr: "Traitement…",       color: "#6B7280", icon: Loader2 },
  awaiting_validation:   { fr: "En attente",        color: AMBER,     icon: AlertTriangle },
  auto_integrated:       { fr: "Intégré (auto)",    color: GREEN,     icon: CheckCircle2 },
  manually_integrated:   { fr: "Intégré",           color: GREEN,     icon: CheckCircle2 },
  rejected:              { fr: "Rejeté",            color: "#94A3B8", icon: XCircle },
  error:                 { fr: "Erreur",            color: CRIMSON,   icon: AlertTriangle },
};

const SOURCE_META = {
  text_paste:    { fr: "Texte collé",       icon: FileText },
  note:          { fr: "Note",              icon: FileText },
  email_paste:   { fr: "Courriel",          icon: FileText },
  csv:           { fr: "CSV",               icon: FileText },
  pdf:           { fr: "PDF",               icon: FileText },
  image:         { fr: "Image",             icon: ImageIcon },
  business_card: { fr: "Carte d'affaires",  icon: ImageIcon },
  screenshot:    { fr: "Capture",           icon: ImageIcon },
  photo:         { fr: "Photo",             icon: ImageIcon },
  voice_note:    { fr: "Note vocale",       icon: FileAudio },
  audio_upload:  { fr: "Audio",             icon: FileAudio },
};

/* ══════════════════════════════════════════════════════════════════
   OAUTH CONNECTORS — Google Contacts + Outlook
   ══════════════════════════════════════════════════════════════════ */
function OAuthPanel({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const load = async () => {
    try { setStatus((await api.get("/intel/oauth/status")).data); }
    catch { toast.error("Erreur statut OAuth."); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Detect ?connected=google|microsoft on return from callback
    const u = new URL(window.location.href);
    const c = u.searchParams.get("connected");
    if (c) { toast.success(`${c === "google" ? "Google" : "Microsoft"} connecté`); load(); onSynced?.(); }
  }, []); // eslint-disable-line

  const connect = async (p) => {
    try {
      const r = await api.get(`/intel/oauth/${p}/start`);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Non disponible.");
    }
  };
  const sync = async (p) => {
    setSyncing(p);
    try {
      const r = await api.post(`/intel/oauth/${p}/sync`);
      toast.success(`${r.data.imported} contact(s) importé(s) depuis ${p}`);
      await load(); onSynced?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync échouée."); }
    setSyncing(null);
  };
  const disconnect = async (p) => {
    if (!window.confirm(`Déconnecter ${p} ?`)) return;
    try { await api.post(`/intel/oauth/${p}/disconnect`); await load(); }
    catch { toast.error("Impossible."); }
  };

  if (!status) return null;
  const providers = [
    { id: "google",    label: "Google Contacts", meta: status.google },
    { id: "microsoft", label: "Microsoft Outlook", meta: status.microsoft },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="oauth-panel">
      <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: CHAMPAGNE }}>
        Connecteurs officiels
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {providers.map(p => (
          <div key={p.id} className="rounded-lg p-3 border"
               style={{ borderColor: p.meta.connected ? CHAMPAGNE : "#E5E7EB",
                          background: p.meta.connected ? "#FBF6EA" : "#fff" }}
               data-testid={`oauth-card-${p.id}`}>
            <div className="flex items-center gap-2">
              {p.id === "google" ? <Mail size={14} style={{ color: NAVY }} />
                                     : <Contact size={14} style={{ color: NAVY }} />}
              <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{p.label}</span>
              {p.meta.available ? (
                <span className="text-[9px] px-1.5 py-0.5 rounded ml-auto"
                      style={{ background: p.meta.connected ? GREEN + "22" : "#F1F5F9",
                                 color: p.meta.connected ? GREEN : "#64748B" }}>
                  {p.meta.connected ? "Connecté" : "Prêt"}
                </span>
              ) : (
                <span className="text-[9px] px-1.5 py-0.5 rounded ml-auto"
                      style={{ background: "#FEF3C7", color: "#B45309" }}>
                  Config admin requise
                </span>
              )}
            </div>
            {p.meta.info?.email && (
              <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                {p.meta.info.email} · {p.meta.info.last_sync_count ?? 0} contacts
              </div>
            )}
            <div className="flex gap-1.5 mt-2">
              {!p.meta.connected ? (
                <button onClick={() => connect(p.id)} disabled={!p.meta.available}
                        data-testid={`oauth-connect-${p.id}-btn`}
                        className="text-[11px] px-3 py-1.5 rounded flex-1 disabled:opacity-40"
                        style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
                  Connecter
                </button>
              ) : (
                <>
                  <button onClick={() => sync(p.id)} disabled={syncing === p.id}
                          data-testid={`oauth-sync-${p.id}-btn`}
                          className="text-[11px] px-3 py-1.5 rounded flex-1"
                          style={{ background: GOLD, color: NAVY, fontWeight: 700 }}>
                    {syncing === p.id ? <Loader2 size={11} className="animate-spin inline" /> : "Sync"}
                  </button>
                  <button onClick={() => disconnect(p.id)}
                          data-testid={`oauth-disconnect-${p.id}-btn`}
                          className="text-[11px] px-2 py-1.5 rounded text-slate-500 hover:text-red-500">
                    <X size={12} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   UNIVERSAL DROPZONE + TABS (upload · voice · text · biz card)
   ══════════════════════════════════════════════════════════════════ */
function IngestPanel({ onIngested }) {
  const [tab, setTab] = useState("upload"); // upload | voice | text | card
  const tabs = [
    { id: "upload", label: "Fichier",         icon: Upload },
    { id: "voice",  label: "Note vocale",     icon: Mic },
    { id: "text",   label: "Texte / Note",    icon: FileText },
    { id: "card",   label: "Carte d'affaires", icon: ImageIcon },
  ];

  return (
    <div className="rounded-2xl shadow-2xl overflow-hidden mb-6"
         style={{ background: `linear-gradient(120deg,${NAVY} 0%,${NAVY_DEEP} 100%)` }}
         data-testid="inbox-ingest-panel">
      <div className="p-6 text-white">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ background: GOLD }}>
            <InboxIcon size={18} style={{ color: NAVY }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              Opportunity Inbox
            </div>
            <h1 className="font-serif text-xl sm:text-2xl leading-tight">
              Déposez, ALTIORA fait le reste
            </h1>
          </div>
        </div>
        <p className="text-[13px] opacity-90 max-w-2xl">
          Une seule boîte, tous les formats : fichiers, notes vocales, textes, cartes d'affaires…
          Le pipeline ALTIORA extrait, fusionne, qualifie et propose la prochaine action.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 mt-5 p-1 rounded-lg" style={{ background: "rgba(0,0,0,0.25)" }}>
          {tabs.map(t => {
            const T = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                      data-testid={`inbox-tab-${t.id}`}
                      className="flex-1 text-[12px] py-2 rounded-md flex items-center justify-center gap-1.5 transition"
                      style={{ background: active ? GOLD : "transparent",
                                 color:      active ? NAVY : "#F5E9C6",
                                 fontWeight: active ? 700 : 500 }}>
                <T size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white p-5">
        {tab === "upload" && <UploadDropzone onIngested={onIngested} kind="upload" />}
        {tab === "card"   && <UploadDropzone onIngested={onIngested} kind="business-card" />}
        {tab === "voice"  && <VoiceRecorder  onIngested={onIngested} />}
        {tab === "text"   && <TextPaste      onIngested={onIngested} />}
      </div>
    </div>
  );
}

function UploadDropzone({ onIngested, kind }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const isCard = kind === "business-card";

  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const path = isCard ? "/intel/inbox/business-card" : "/intel/inbox/upload";
      const r = await api.post(path, fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.status === "auto_integrated"
        ? `Intégré automatiquement (${(r.data.confidence * 100).toFixed(0)} % confiance)`
        : "Ajouté à la boîte pour validation");
      onIngested?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible.");
    }
    setBusy(false);
  };

  return (
    <>
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files?.[0]); }}
        onClick={() => ref.current?.click()}
        data-testid={`inbox-dropzone-${kind}`}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${drag ? "scale-[1.01]" : ""}`}
        style={{ borderColor: drag ? GOLD : CHAMPAGNE + "88",
                    background: drag ? CREAM : "#FAFAFA" }}
      >
        {busy ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 size={28} className="animate-spin" style={{ color: NAVY }} />
            <div className="text-[13px]">Analyse IA en cours…</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-14 h-14 rounded-full flex items-center justify-center"
                 style={{ background: CREAM }}>
              {isCard ? <ImageIcon size={22} style={{ color: NAVY }} />
                       : <Upload size={22} style={{ color: NAVY }} />}
            </div>
            <div className="text-[14px] font-semibold" style={{ color: NAVY }}>
              {isCard ? "Déposez une carte d'affaires (recto + verso combinés)"
                        : "Glissez ici ou cliquez"}
            </div>
            <div className="text-[11px] text-slate-500">
              {isCard ? "PNG/JPEG · Recto et verso empilés dans une seule image"
                        : "CSV · PDF · Image · Audio · max 25 Mo"}
            </div>
          </div>
        )}
        <input ref={ref} type="file"
                 accept={isCard ? "image/*" : ".csv,.pdf,image/*,audio/*,.txt"}
                 className="hidden"
                 onChange={e => send(e.target.files?.[0])}
                 data-testid={`inbox-file-input-${kind}`} />
      </div>
      <p className="text-[10px] text-slate-500 mt-2">
        L'IA ALTIORA extrait automatiquement : nom, courriel, téléphone, entreprise, secteur, ville, LinkedIn, notes.
      </p>
    </>
  );
}

function VoiceRecorder({ onIngested }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => () => cleanup(), []); // eslint-disable-line
  const cleanup = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await send(blob, "note-vocale.webm");
      };
      mr.start(); mediaRef.current = mr;
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      setRecording(true);
    } catch (e) {
      toast.error("Microphone refusé.");
    }
  };
  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  const send = async (fileOrBlob, name = "audio") => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", fileOrBlob, name);
      const r = await api.post("/intel/inbox/audio", fd,
                                    { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.status === "auto_integrated"
        ? `Note vocale intégrée (${(r.data.confidence * 100).toFixed(0)} %)`
        : "Note vocale ajoutée pour validation");
      onIngested?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de transcrire.");
    }
    setBusy(false);
  };

  return (
    <div className="text-center py-6">
      <div className="flex flex-col items-center gap-4">
        {busy ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 size={40} className="animate-spin" style={{ color: NAVY }} />
            <div className="text-[13px]">Transcription Whisper + extraction IA…</div>
          </div>
        ) : recording ? (
          <>
            <button onClick={stop} data-testid="inbox-voice-stop-btn"
                    className="w-24 h-24 rounded-full flex items-center justify-center transition hover:scale-105 shadow-lg"
                    style={{ background: CRIMSON, color: "#fff" }}>
              <MicOff size={36} />
            </button>
            <div className="font-serif text-2xl" style={{ color: NAVY }}>
              {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
            </div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: CRIMSON }}>
              ● Enregistrement…
            </div>
          </>
        ) : (
          <>
            <button onClick={start} data-testid="inbox-voice-start-btn"
                    className="w-24 h-24 rounded-full flex items-center justify-center transition hover:scale-105 shadow-lg"
                    style={{ background: NAVY, color: "#fff" }}>
              <Mic size={36} />
            </button>
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
              Cliquez pour enregistrer votre debrief post-rendez-vous
            </div>
            <div className="text-[11px] text-slate-500 max-w-md">
              Ex. « J'ai rencontré M. Dupont. Il veut acheter une maison dans deux ans,
              a deux enfants et cherche une assurance vie. » — l'IA crée la fiche + les tâches.
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span>ou</span>
              <button onClick={() => fileInputRef.current?.click()}
                      data-testid="inbox-voice-upload-btn"
                      className="underline hover:text-slate-800">
                importer un fichier audio (mp3, wav, m4a…)
              </button>
              <input ref={fileInputRef} type="file" accept="audio/*" className="hidden"
                       onChange={e => e.target.files?.[0] && send(e.target.files[0], e.target.files[0].name)} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TextPaste({ onIngested }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/intel/inbox/text",
                                    { text, source_kind: "note" });
      toast.success(r.data.status === "auto_integrated"
        ? "Note intégrée automatiquement"
        : "Note ajoutée à la boîte");
      setText("");
      onIngested?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    }
    setBusy(false);
  };

  return (
    <div>
      <textarea
        value={text} onChange={e => setText(e.target.value)}
        placeholder="Collez un courriel, une note de rendez-vous, une conversation… l'IA identifie les contacts, les tâches et les prochaines actions."
        rows={6}
        data-testid="inbox-text-input"
        className="w-full text-[13px] p-3 rounded-lg border resize-none focus:outline-none focus:ring-2"
        style={{ borderColor: CHAMPAGNE + "55" }} />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-slate-500">{text.length}/20 000 caractères</span>
        <button onClick={submit} disabled={busy || !text.trim()}
                data-testid="inbox-text-submit-btn"
                className="text-[12px] px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {busy ? "Analyse…" : "Extraire avec l'IA"}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   INBOX ITEMS LIST
   ══════════════════════════════════════════════════════════════════ */
function ItemCard({ item, onApprove, onReject, onDelete, onEdited }) {
  const [expanded, setExpanded] = useState(item.status === "awaiting_validation");
  const [editing, setEditing]   = useState(false);
  const [drafts, setDrafts]     = useState(item.contacts_processed || []);
  const [savingEdit, setSavingEdit] = useState(false);

  const meta   = STATUS_META[item.status] || STATUS_META.processing;
  const srcMeta = SOURCE_META[item.source_kind] || { fr: item.source_kind, icon: FileText };
  const StatusIcon = meta.icon;
  const SrcIcon    = srcMeta.icon;
  const contacts = item.contacts_processed || [];

  const saveEdits = async () => {
    setSavingEdit(true);
    try {
      const r = await api.post(`/intel/inbox/items/${item.id}/edit`,
                                    { contacts_processed: drafts });
      toast.success("Modifications enregistrées");
      setDrafts(r.data.contacts);
      onEdited?.();
      setEditing(false);
    } catch (e) {
      toast.error("Impossible d'enregistrer.");
    }
    setSavingEdit(false);
  };

  const patchContact = (idx, field, value) => {
    setDrafts(d => d.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border transition"
         style={{ borderColor: item.status === "awaiting_validation" ? AMBER + "88" : "#E5E7EB" }}
         data-testid={`inbox-item-${item.id}`}>
      <div className="p-4 cursor-pointer" onClick={() => setExpanded(x => !x)}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: CREAM }}>
            <SrcIcon size={16} style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-semibold" style={{ color: NAVY }}>
                {contacts[0]?.name || item.filename || item.extraction?.summary_fr?.slice(0, 60) || srcMeta.fr}
              </span>
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: meta.color + "1A", color: meta.color }}>
                <StatusIcon size={9} className="inline mb-0.5" /> {meta.fr}
              </span>
              {item.confidence > 0 && (
                <span className="text-[10px] text-slate-500">
                  {(item.confidence * 100).toFixed(0)}% conf.
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{srcMeta.fr}</span>
              {contacts.length > 0 && <><span>·</span><span>{contacts.length} contact(s)</span></>}
              {item.extraction?.tasks?.length > 0 && (
                <><span>·</span><span>{item.extraction.tasks.length} tâche(s)</span></>
              )}
              <span>·</span>
              <span>{new Date(item.created_at).toLocaleString("fr-CA")}</span>
            </div>
          </div>
          <ChevronRight size={14} className={`shrink-0 transition ${expanded ? "rotate-90" : ""}`}
                          style={{ color: NAVY }} />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {item.extraction?.summary_fr && (
            <p className="text-[12px] italic text-slate-600">{item.extraction.summary_fr}</p>
          )}

          {item.error_fr && (
            <p className="text-[12px] p-2 rounded" style={{ background: CRIMSON + "1A", color: CRIMSON }}>
              {item.error_fr}
            </p>
          )}

          {/* Contacts */}
          {contacts.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                Contacts extraits
              </div>
              <div className="space-y-2">
                {(editing ? drafts : contacts).map((c, idx) => (
                  <div key={idx} className="rounded p-3"
                       style={{ background: CREAM, border: `1px solid ${CHAMPAGNE}33` }}
                       data-testid={`inbox-item-${item.id}-contact-${idx}`}>
                    {editing ? (
                      <div className="grid grid-cols-2 gap-2 text-[12px]">
                        {["name","email","phone","company","sector_fr","city"].map(f => (
                          <input key={f} value={c[f] || ""}
                                   onChange={e => patchContact(idx, f, e.target.value)}
                                   placeholder={f}
                                   className="p-1.5 rounded border"
                                   style={{ borderColor: CHAMPAGNE + "55" }} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-[12px] space-y-0.5">
                        <div className="font-semibold" style={{ color: NAVY }}>{c.name || "—"}</div>
                        <div className="text-slate-600">
                          {[c.email, c.phone, c.company, c.role, c.sector_fr, c.city].filter(Boolean).join(" · ")}
                        </div>
                        {c.notes_fr && <div className="italic text-slate-500 mt-1">« {c.notes_fr} »</div>}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-bold text-white"
                                style={{ background: c.priority === "high" ? GREEN :
                                                        c.priority === "verify" ? AMBER : CHAMPAGNE }}>
                            {c.priority} · {Math.round(c.score || 0)}
                          </span>
                          {c.is_duplicate && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: AMBER + "22", color: AMBER }}>
                              Doublon détecté
                            </span>
                          )}
                          {c.est_value_cad > 0 && (
                            <span className="text-[10px]" style={{ color: GREEN }}>
                              {c.est_value_cad.toLocaleString("fr-CA")} $
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks */}
          {item.extraction?.tasks?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                Tâches inférées
              </div>
              {item.extraction.tasks.map((t, i) => (
                <div key={i} className="text-[12px] p-2 rounded flex items-center gap-2"
                     style={{ background: "#F1F5F9" }}>
                  <Clock size={12} style={{ color: NAVY }} />
                  <span className="flex-1">{t.title_fr}</span>
                  {t.due_hint && <span className="text-slate-500 text-[10px]">· {t.due_hint}</span>}
                </div>
              ))}
            </div>
          )}

          {/* CTAs */}
          {item.status === "awaiting_validation" && !editing && (
            <div className="flex gap-2 pt-2">
              <button onClick={() => onApprove(item.id)}
                      data-testid={`inbox-approve-${item.id}`}
                      className="flex-1 text-[12px] py-2 rounded-lg font-semibold flex items-center justify-center gap-1"
                      style={{ background: GREEN, color: "#fff" }}>
                <CheckCircle2 size={13} /> Valider et intégrer
              </button>
              <button onClick={() => setEditing(true)}
                      data-testid={`inbox-edit-${item.id}`}
                      className="text-[12px] py-2 px-3 rounded-lg flex items-center gap-1"
                      style={{ background: "#F1F5F9", color: NAVY }}>
                <Edit3 size={13} /> Corriger
              </button>
              <button onClick={() => onReject(item.id)}
                      data-testid={`inbox-reject-${item.id}`}
                      className="text-[12px] py-2 px-3 rounded-lg flex items-center gap-1"
                      style={{ background: "#F1F5F9", color: CRIMSON }}>
                <XCircle size={13} />
              </button>
            </div>
          )}
          {editing && (
            <div className="flex gap-2 pt-2">
              <button onClick={saveEdits} disabled={savingEdit}
                      className="flex-1 text-[12px] py-2 rounded-lg font-semibold flex items-center justify-center gap-1"
                      style={{ background: NAVY, color: "#fff" }}>
                {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Enregistrer
              </button>
              <button onClick={() => { setEditing(false); setDrafts(contacts); }}
                      className="text-[12px] py-2 px-3 rounded-lg"
                      style={{ background: "#F1F5F9", color: NAVY }}>
                Annuler
              </button>
            </div>
          )}

          {item.status !== "awaiting_validation" && (
            <div className="flex justify-end pt-1">
              <button onClick={() => onDelete(item.id)}
                      data-testid={`inbox-delete-${item.id}`}
                      className="text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-1">
                <Trash2 size={11} /> Supprimer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PIPELINE VISUALIZER — 11 stages
   ══════════════════════════════════════════════════════════════════ */
function PipelineFlow() {
  const stages = [
    "Collecte", "Extraction", "Fusion", "Doublons", "Conformité",
    "Enrichissement", "Score", "Qualification", "Recommandation",
    "Validation", "Intégration",
  ];
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6 overflow-x-auto"
         data-testid="inbox-pipeline-flow">
      <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: CHAMPAGNE }}>
        Pipeline universel ALTIORA
      </div>
      <div className="flex items-center gap-1 min-w-max">
        {stages.map((s, i) => (
          <div key={s} className="flex items-center">
            <div className="text-[11px] px-2.5 py-1 rounded-full whitespace-nowrap"
                 style={{ background: CREAM, color: NAVY, border: `1px solid ${CHAMPAGNE}55` }}>
              {s}
            </div>
            {i < stages.length - 1 && <ArrowRight size={11} style={{ color: CHAMPAGNE }} />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════════════ */
export default function InboxPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | awaiting | integrated | rejected

  const refresh = async () => {
    try {
      const [i, s] = await Promise.all([
        api.get("/intel/inbox/items?limit=50"),
        api.get("/intel/inbox/stats"),
      ]);
      setItems(i.data.items);
      setStats(s.data);
    } catch (e) {
      toast.error("Erreur de chargement.");
    }
  };
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const approve = async (id) => {
    try { await api.post(`/intel/inbox/items/${id}/approve`); toast.success("Intégré"); await refresh(); }
    catch { toast.error("Impossible."); }
  };
  const reject = async (id) => {
    try { await api.post(`/intel/inbox/items/${id}/reject`); await refresh(); }
    catch { toast.error("Impossible."); }
  };
  const remove = async (id) => {
    try { await api.delete(`/intel/inbox/items/${id}`); await refresh(); }
    catch { toast.error("Impossible."); }
  };

  const filtered = items.filter(it => {
    if (filter === "all") return true;
    if (filter === "awaiting") return it.status === "awaiting_validation";
    if (filter === "integrated") return ["auto_integrated","manually_integrated"].includes(it.status);
    if (filter === "rejected") return ["rejected","error"].includes(it.status);
    return true;
  });

  if (loading) return (
    <PageShell testid="inbox-loading">
      <TopBar />
      <div className="p-8 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
    </PageShell>
  );

  return (
    <PageShell testid="inbox-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow="ALTIORA · Opportunity Inbox"
          title="Une seule boîte, tous les formats"
          subtitle="Déposez, dictez ou collez — l'IA fusionne, dédoublonne, géolocalise et qualifie automatiquement chaque signal."
          onBack={() => nav("/app/intel-commerciale")}
          testid="inbox-header" />

        <IngestPanel onIngested={refresh} />

        <OAuthPanel onSynced={refresh} />

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="inbox-stats">
            <StatCard label="Total"           value={stats.total} icon={InboxIcon} />
            <StatCard label="En attente"      value={stats.awaiting_validation} icon={AlertTriangle} color={AMBER} />
            <StatCard label="Auto-intégrés"   value={stats.auto_integrated}     icon={CheckCircle2}  color={GREEN} />
            <StatCard label="Validés"         value={stats.manually_integrated} icon={CheckCircle2} />
            <StatCard label="Erreurs"         value={stats.errors}              icon={XCircle}     color={CRIMSON} />
          </div>
        )}

        <PipelineFlow />

        {/* Filter tabs */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1 p-1 rounded-lg bg-white shadow-sm">
            {[
              { id: "all",         label: "Tout" },
              { id: "awaiting",    label: "À valider" },
              { id: "integrated",  label: "Intégrés" },
              { id: "rejected",    label: "Rejetés" },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                      data-testid={`inbox-filter-${f.id}`}
                      className="text-[11px] px-3 py-1.5 rounded-md transition"
                      style={{ background: filter === f.id ? NAVY : "transparent",
                                 color:      filter === f.id ? "#fff" : NAVY,
                                 fontWeight: filter === f.id ? 600 : 500 }}>
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={refresh} data-testid="inbox-refresh-btn"
                  className="text-[11px] flex items-center gap-1 text-slate-600 hover:text-slate-900">
            <RefreshCw size={11} /> Actualiser
          </button>
        </div>

        {/* Items list */}
        <div className="space-y-3" data-testid="inbox-items-list">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center"
                   style={{ background: CREAM }}>
                <InboxIcon size={22} style={{ color: NAVY }} />
              </div>
              <div className="mt-3 text-[14px] font-semibold" style={{ color: NAVY }}>
                Boîte vide
              </div>
              <div className="text-[12px] text-slate-500 mt-1">
                Déposez votre premier élément ci-dessus — l'IA transforme tout en opportunité qualifiée.
              </div>
            </div>
          ) : (
            filtered.map(it => (
              <ItemCard key={it.id} item={it}
                          onApprove={approve} onReject={reject} onDelete={remove}
                          onEdited={refresh} />
            ))
          )}
        </div>
      </div>
    </PageShell>
  );
}

function StatCard({ label, value, icon: I, color }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-3">
      <I size={14} style={{ color: color || CHAMPAGNE }} />
      <div className="font-serif text-xl mt-1 font-bold" style={{ color: NAVY }}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
    </div>
  );
}
